// src/pages/ActiveDirectoryComparison.jsx - Compare two AD audits for the same client
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Grid, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Compare as CompareIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { adAuditApi } from '../services/api';
import { toast } from 'react-toastify';

const SEV_COLORS = {
  Critical: '#e74c3c',
  High: '#e67e22',
  Medium: '#f39c12',
  Low: '#3498db',
  Info: '#95a5a6'
};

const ActiveDirectoryComparison = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();

  const [audits, setAudits] = useState([]);
  const [currentAudit, setCurrentAudit] = useState(null);
  const [compareAuditId, setCompareAuditId] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadData();
  }, [auditId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const auditRes = await adAuditApi.getAudit(auditId);
      setCurrentAudit(auditRes.data);

      // Load all audits for same client
      const allRes = await adAuditApi.getAudits({ client_id: auditRes.data.client_id });
      const others = (allRes.data || []).filter(
        a => String(a.id) !== String(auditId) && a.status !== 'processing'
      );
      setAudits(others);
    } catch {
      toast.error('Failed to load audit data');
      navigate(`/active-directory-audits/${auditId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!compareAuditId) {
      toast.error('Please select an audit to compare against');
      return;
    }
    try {
      setComparing(true);
      setComparison(null);
      const res = await adAuditApi.compareAudits(parseInt(auditId), parseInt(compareAuditId));
      setComparison(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Comparison failed');
    } finally {
      setComparing(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!comparison) return;
    try {
      setDownloading(true);
      await adAuditApi.downloadComparisonReport(
        comparison.older_audit.id,
        comparison.newer_audit.id
      );
      toast.success('Comparison report downloaded!');
    } catch {
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const getTrendIcon = (change) => {
    if (change > 0) return <TrendingUpIcon sx={{ color: '#e74c3c', fontSize: 18 }} />;
    if (change < 0) return <TrendingDownIcon sx={{ color: '#27ae60', fontSize: 18 }} />;
    return null;
  };

  const getScoreChangeColor = (change) => {
    if (change > 0) return '#e74c3c';
    if (change < 0) return '#27ae60';
    return '#95a5a6';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#6B3FA0' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/active-directory-audits/${auditId}`)}
          sx={{ mb: 2, color: '#6B3FA0' }}
        >
          Back to Audit
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>
          AD Audit Comparison
        </Typography>
        {currentAudit && (
          <Typography variant="body2" color="text.secondary">
            {currentAudit.client_name} — comparing against: {currentAudit.audit_name}
          </Typography>
        )}
      </Box>

      {/* Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#4A2B70' }}>
            Select Audits to Compare
          </Typography>
          {audits.length === 0 ? (
            <Alert severity="warning">
              No other analyzed audits found for this client. Run at least two AD audits to use the comparison feature.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Current audit</Typography>
                <Chip
                  label={currentAudit?.audit_name}
                  sx={{ bgcolor: '#6B3FA0', color: 'white', fontWeight: 600 }}
                />
              </Box>
              <CompareIcon sx={{ color: '#6B3FA0' }} />
              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel>Compare with</InputLabel>
                <Select
                  value={compareAuditId}
                  onChange={(e) => setCompareAuditId(e.target.value)}
                  label="Compare with"
                >
                  {audits.map(a => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.audit_name} ({a.audit_date ? new Date(a.audit_date).toLocaleDateString() : 'No date'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleCompare}
                disabled={comparing || !compareAuditId}
                startIcon={comparing ? <CircularProgress size={18} color="inherit" /> : <CompareIcon />}
                sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' }, alignSelf: 'center' }}
              >
                {comparing ? 'Comparing...' : 'Compare'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Comparison Results */}
      {comparison && (
        <Box>
          {/* Download Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadReport}
              disabled={downloading}
              sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#229954' } }}
            >
              {downloading ? 'Downloading...' : 'Download Comparison Report'}
            </Button>
          </Box>

          {/* Risk Score Trend */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#4A2B70' }}>
                Risk Score Trend
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#F8F9FA', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Previous Audit</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c3e50', my: 1 }}>
                      {comparison.older_audit.score.toFixed(1)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {comparison.older_audit.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(comparison.older_audit.date).toLocaleDateString()}
                    </Typography>
                    <br />
                    <Chip
                      label={`${comparison.older_audit.total_findings} findings`}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#F8F9FA', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Change</Typography>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: getScoreChangeColor(comparison.score_trend.change),
                        my: 1
                      }}
                    >
                      {comparison.score_trend.change > 0 ? '+' : ''}
                      {comparison.score_trend.change.toFixed(1)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: getScoreChangeColor(comparison.score_trend.change) }}
                    >
                      {comparison.score_trend.percent_change > 0 ? '+' : ''}
                      {comparison.score_trend.percent_change}%
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {comparison.score_trend.change < 0 ? (
                        <Alert severity="success" sx={{ py: 0 }}>Score Improved</Alert>
                      ) : comparison.score_trend.change > 0 ? (
                        <Alert severity="error" sx={{ py: 0 }}>Score Worsened</Alert>
                      ) : (
                        <Alert severity="info" sx={{ py: 0 }}>No Change</Alert>
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#F8F9FA', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Current Audit</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c3e50', my: 1 }}>
                      {comparison.newer_audit.score.toFixed(1)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {comparison.newer_audit.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(comparison.newer_audit.date).toLocaleDateString()}
                    </Typography>
                    <br />
                    <Chip
                      label={`${comparison.newer_audit.total_findings} findings`}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Severity Trends */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#4A2B70' }}>
                Finding Severity Trends
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#F3EEFA' }}>
                    <TableRow>
                      <TableCell><strong>Severity</strong></TableCell>
                      <TableCell align="center"><strong>Previous</strong></TableCell>
                      <TableCell align="center"><strong>Change</strong></TableCell>
                      <TableCell align="center"><strong>Current</strong></TableCell>
                      <TableCell align="center"><strong>Trend</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {['critical', 'high', 'medium', 'low', 'info'].map(sev => {
                      const t = comparison.severity_trends[sev];
                      const sevCap = sev.charAt(0).toUpperCase() + sev.slice(1);
                      return (
                        <TableRow key={sev}>
                          <TableCell>
                            <Chip
                              label={sevCap}
                              size="small"
                              sx={{
                                bgcolor: SEV_COLORS[sevCap] || '#95a5a6',
                                color: 'white',
                                fontWeight: 700,
                                minWidth: 75
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {t.older_count}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              {getTrendIcon(t.change)}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: getScoreChangeColor(t.change)
                                }}
                              >
                                {t.change > 0 ? '+' : ''}{t.change}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {t.newer_count}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              sx={{ color: getScoreChangeColor(t.change) }}
                            >
                              {t.change > 0 ? '↑ Increased' : t.change < 0 ? '↓ Decreased' : '→ Stable'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Remediation Progress & New Findings */}
          <Grid container spacing={3}>
            {/* Remediated */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingDownIcon sx={{ color: '#27ae60' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#27ae60' }}>
                      Remediated ({comparison.remediated_count})
                    </Typography>
                  </Box>
                  {comparison.remediated_findings.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No findings were remediated between audits.
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#D5F5E3' }}>
                          <TableRow>
                            <TableCell><strong>Severity</strong></TableCell>
                            <TableCell><strong>Finding</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {comparison.remediated_findings.map((f, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Chip
                                  label={f.severity}
                                  size="small"
                                  sx={{
                                    bgcolor: SEV_COLORS[f.severity] || '#95a5a6',
                                    color: 'white',
                                    fontWeight: 700
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {f.finding_name}
                                </Typography>
                                {f.category && (
                                  <Typography variant="caption" color="text.secondary">
                                    {f.category}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* New Findings */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUpIcon sx={{ color: '#e74c3c' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#e74c3c' }}>
                      New Findings ({comparison.new_findings_count})
                    </Typography>
                  </Box>
                  {comparison.new_findings.length === 0 ? (
                    <Alert severity="success">No new findings identified. Great progress!</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#FADBD8' }}>
                          <TableRow>
                            <TableCell><strong>Severity</strong></TableCell>
                            <TableCell><strong>Finding</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {comparison.new_findings.map((f, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Chip
                                  label={f.severity}
                                  size="small"
                                  sx={{
                                    bgcolor: SEV_COLORS[f.severity] || '#95a5a6',
                                    color: 'white',
                                    fontWeight: 700
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {f.finding_name}
                                </Typography>
                                {f.category && (
                                  <Typography variant="caption" color="text.secondary">
                                    {f.category}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ActiveDirectoryComparison;
